import { useEffect, useRef, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useGetInvoiceForCorrectionQuery } from '../Service/correctionApi';
import type { CreateInvoiceForm } from '../Types/createInvoiceFormTypes';
import { VatRateType, InvoiceType } from '../Types/invoicesTypes';
import { invoiceLogger } from '../utils/invoiceLogger';

invoiceLogger.debug(
  '%c🔥 useCorrectionPrefill.ts MODULE LOADED 🔥',
  'background: red; color: white; font-size: 20px; padding: 10px;'
);

/**
 * Custom hook for pre-filling correction invoice form with base invoice data.
 *
 * Purpose:
 * - Fetches base invoice using validated endpoint (with business rule checks)
 * - Auto-fills all form fields with base invoice values
 * - User can modify any field before submitting correction
 * - Handles error states (draft, cancelled, correction-of-correction)
 *
 * Usage:
 * ```tsx
 * const { baseInvoice, isLoading, error } = useCorrectionPrefill(
 *   baseInvoiceId,
 *   setValue
 * );
 * ```
 *
 * @param baseInvoiceId - ID of the invoice to be corrected
 * @param setValue - React Hook Form setValue function
 * @returns Object with baseInvoice data, loading state, and error
 */
export const useCorrectionPrefill = (
  baseInvoiceId: number | undefined,
  setValue: UseFormSetValue<CreateInvoiceForm>
) => {
  invoiceLogger.debug(
    `[useCorrectionPrefill] 🚀 Hook called with baseInvoiceId: ${baseInvoiceId} at ${Date.now()}`
  );

  // Fetch base invoice with validation (backend checks: not draft, not cancelled, not correction)
  const {
    data: baseInvoice,
    isLoading,
    error,
    isFetching,
    isSuccess,
    isUninitialized,
  } = useGetInvoiceForCorrectionQuery(baseInvoiceId!, {
    skip: !baseInvoiceId || baseInvoiceId <= 0,
    refetchOnMountOrArgChange: 1, // Force refetch if data is older than 1 second
  });

  invoiceLogger.debug('[useCorrectionPrefill] 📊 Query state:', {
    baseInvoiceId,
    skip: !baseInvoiceId || baseInvoiceId <= 0,
    isLoading,
    isFetching,
    isSuccess,
    isUninitialized,
    hasError: !!error,
    error: error,
    hasData: !!baseInvoice,
    baseInvoice_type: typeof baseInvoice,
    baseInvoice_keys: baseInvoice ? Object.keys(baseInvoice).length : 0,
    baseInvoiceId_fromData: baseInvoice?.id,
  });

  if (baseInvoice) {
    invoiceLogger.debug('[useCorrectionPrefill] ✅ baseInvoice DATA:', baseInvoice);
  } else {
    invoiceLogger.warn('[useCorrectionPrefill] ⚠️ baseInvoice is UNDEFINED/NULL');
  }

  invoiceLogger.debug(
    '[useCorrectionPrefill] 🔑 baseInvoice?.id value:',
    baseInvoice?.id,
    'typeof:',
    typeof baseInvoice?.id
  );

  // Track if we've already processed this invoice to prevent duplicate processing
  const processedInvoiceIdRef = useRef<number | null>(null);
  invoiceLogger.debug(
    '[useCorrectionPrefill] 📝 processedInvoiceIdRef.current:',
    processedInvoiceIdRef.current
  );

  // Track prefilling state to prevent form render before data is populated
  const [isPrefilling, setIsPrefilling] = useState(true);

  /**
   * Normalizes VAT type from backend (camelCase) to frontend (PascalCase).
   * Backend: "vatRehab" → Frontend: "VatRehab"
   */
  const normalizeVatType = (vatType: string | undefined): VatRateType | undefined => {
    if (!vatType) return undefined;

    // If already valid enum, return as-is
    if (Object.values(VatRateType).includes(vatType as VatRateType)) {
      return vatType as VatRateType;
    }

    // Capitalize first letter: vatRehab → VatRehab
    const normalized = vatType.charAt(0).toUpperCase() + vatType.slice(1);

    if (Object.values(VatRateType).includes(normalized as VatRateType)) {
      return normalized as VatRateType;
    }

    // Fallback: return original (will likely cause validation error)
    invoiceLogger.warn('[useCorrectionPrefill] Unknown vatType:', vatType);
    return vatType as VatRateType;
  };

  useEffect(() => {
    invoiceLogger.debug(
      `%c[useCorrectionPrefill] ⚡⚡⚡ useEffect CALLED at ${Date.now()}`,
      'background: yellow; color: black; font-size: 16px; font-weight: bold;'
    );
    invoiceLogger.debug(`[useCorrectionPrefill] ⚡ useEffect triggered at ${Date.now()}:`, {
      hasBaseInvoice: !!baseInvoice,
      hasSetValue: !!setValue,
      baseInvoiceId: baseInvoice?.id,
      alreadyProcessed: processedInvoiceIdRef.current === baseInvoice?.id,
    });

    if (!baseInvoice || !setValue) {
      invoiceLogger.warn(
        '[useCorrectionPrefill] ❌ Early return - missing baseInvoice or setValue'
      );
      setIsPrefilling(false);
      return;
    }

    // Start prefilling
    setIsPrefilling(true);

    // TEMPORARY: Skip duplicate check to see if useEffect runs
    invoiceLogger.debug(
      '[useCorrectionPrefill] 🚨 PROCESSING INVOICE (ref check disabled):',
      baseInvoice.id
    );

    invoiceLogger.debug('[useCorrectionPrefill] 🔵 Starting prefill with baseInvoice:', {
      invoiceId: baseInvoice.id,
      reservationId: baseInvoice.reservationId,
      hasReservationId: !!baseInvoice.reservationId,
    });

    // ===================================================================
    // CORRECTION-SPECIFIC FIELDS
    // ===================================================================
    setValue('correctionInvoiceId', baseInvoice.id, { shouldValidate: false });
    // Force correction type (should already be set in defaultValues, but ensure it's not overridden)
    setValue('type', InvoiceType.Correction as any, { shouldValidate: false });

    // ===================================================================
    // BASIC FIELDS (from base invoice)
    // ===================================================================
    if (baseInvoice.reservationId) {
      invoiceLogger.debug(
        '[useCorrectionPrefill] ✅ Setting reservationId:',
        baseInvoice.reservationId
      );
      setValue('reservationId', baseInvoice.reservationId, { shouldValidate: false });
    } else {
      invoiceLogger.debug('[useCorrectionPrefill] ⚠️ No reservationId in baseInvoice');
    }

    if (baseInvoice.companyId) {
      invoiceLogger.debug('[useCorrectionPrefill] ✅ Setting companyId:', baseInvoice.companyId);
      setValue('companyId', baseInvoice.companyId, { shouldValidate: false });
    } else {
      invoiceLogger.debug('[useCorrectionPrefill] ⚠️ No companyId in baseInvoice');
    }

    if (baseInvoice.currency) {
      setValue('currency', baseInvoice.currency, { shouldValidate: false });
    }

    if (baseInvoice.scheme !== undefined && baseInvoice.scheme !== null) {
      setValue('scheme', baseInvoice.scheme, { shouldValidate: false });
    }

    // ===================================================================
    // DOCUMENT DATA (inherit from base, update issueDate to today)
    // ===================================================================
    // Issue date should be TODAY (correction is issued now)
    const today = new Date().toISOString().slice(0, 10);

    invoiceLogger.debug('[useCorrectionPrefill] 📅 Setting issueDate to TODAY:', {
      today,
      todayISO: new Date().toISOString(),
      todayTimestamp: new Date().getTime(),
      baseInvoiceIssueDate: baseInvoice.issueDate,
    });

    setValue('issueDate', today, { shouldValidate: false });

    // Sale date inherits from base invoice (extract YYYY-MM-DD for <input type="date">)
    if (baseInvoice.saleDate) {
      setValue('saleDate', baseInvoice.saleDate.split('T')[0], { shouldValidate: false });
    }

    if (baseInvoice.placeOfIssue) {
      setValue('placeOfIssue', baseInvoice.placeOfIssue, { shouldValidate: false });
    }

    if (baseInvoice.paymentMethod) {
      // Capitalize paymentMethod: backend sends 'transfer' → Select expects 'Transfer'
      const capitalizedPaymentMethod = baseInvoice.paymentMethod
        ? ((baseInvoice.paymentMethod.charAt(0).toUpperCase() +
            baseInvoice.paymentMethod.slice(1)) as any)
        : null;
      setValue('paymentMethod', capitalizedPaymentMethod, { shouldValidate: false });
    }

    if (baseInvoice.bankAccount) {
      setValue('bankAccount', baseInvoice.bankAccount, { shouldValidate: false });
    }

    // Payment due date can be recalculated or inherited (extract YYYY-MM-DD for <input type="date">)
    if (baseInvoice.paymentDueDate) {
      setValue('paymentDueDate', baseInvoice.paymentDueDate.split('T')[0], {
        shouldValidate: false,
      });
    }

    // ===================================================================
    // CORRECTION REASON (Comments field - DO NOT prefill from base)
    // ===================================================================
    // IMPORTANT: Do NOT copy comments from base invoice.
    // User MUST provide a new correction reason.
    // Leave comments field empty to force user input.
    setValue('comments', '', { shouldValidate: false });

    // ===================================================================
    // SELLER (auto-fill from base invoice)
    // ===================================================================
    // Backend może zwracać wszystko w sellerAddress (np. "Solna 10u, 25-006 Kielce")
    // Próbujemy automatycznie rozdzielić na ulicę+numer i kod+miasto
    if (baseInvoice.sellerName) {
      setValue('sellerName', baseInvoice.sellerName, { shouldValidate: false });
    }

    // If sellerStreetNo is provided separately, use it
    if (baseInvoice.sellerStreetNo) {
      setValue('sellerStreetNo', baseInvoice.sellerStreetNo, { shouldValidate: false });
    }

    // If sellerAddress contains comma-separated parts, try to split street from city
    if (baseInvoice.sellerAddress) {
      const addressParts = baseInvoice.sellerAddress.split(',').map((s: string) => s.trim());

      // If we have 2+ parts and sellerStreetNo is empty, assume first part is street
      if (addressParts.length >= 2 && !baseInvoice.sellerStreetNo) {
        const [street, ...cityParts] = addressParts;
        setValue('sellerStreetNo', street, { shouldValidate: false });
        setValue('sellerAddress', cityParts.join(', '), { shouldValidate: false });
      } else {
        // Otherwise use full address as-is
        setValue('sellerAddress', baseInvoice.sellerAddress, { shouldValidate: false });
      }
    }

    if (baseInvoice.sellerNip) {
      setValue('sellerNip', baseInvoice.sellerNip, { shouldValidate: false });
    }

    // ===================================================================
    // BUYER (auto-fill from base invoice)
    // ===================================================================
    if (baseInvoice.buyerName) {
      setValue('buyerName', baseInvoice.buyerName, { shouldValidate: false });
    }

    if (baseInvoice.buyerStreetNo) {
      setValue('buyerStreetNo', baseInvoice.buyerStreetNo, { shouldValidate: false });
    }

    // Smart-split logic for buyerAddress (same as seller)
    if (baseInvoice.buyerAddress) {
      const addressParts = baseInvoice.buyerAddress.split(',').map((s: string) => s.trim());

      // If backend sends combined address and no separate street field
      if (addressParts.length >= 2 && !baseInvoice.buyerStreetNo) {
        const [street, ...cityParts] = addressParts;
        setValue('buyerStreetNo', street, { shouldValidate: false });
        setValue('buyerAddress', cityParts.join(', '), { shouldValidate: false });
      } else {
        // Otherwise use full address as-is
        setValue('buyerAddress', baseInvoice.buyerAddress, { shouldValidate: false });
      }
    }

    if (baseInvoice.buyerNip) {
      setValue('buyerNip', baseInvoice.buyerNip, { shouldValidate: false });
    }

    setValue('buyerCountryIso', (baseInvoice.buyerCountryIso || 'PL').toUpperCase(), {
      shouldValidate: false,
    });
    setValue('isBuyerVatEu', baseInvoice.isBuyerVatEu ?? false, {
      shouldValidate: false,
    });
    setValue('buyerIsJst', baseInvoice.buyerIsJst ?? false, {
      shouldValidate: false,
    });

    // ===================================================================
    // RECIPIENT (auto-fill from base invoice when different than buyer)
    // ===================================================================
    const hasDifferentRecipient = Boolean(
      baseInvoice.recipientName || baseInvoice.recipientAddress || baseInvoice.recipientNip
    );

    setValue('useDifferentRecipient', hasDifferentRecipient, { shouldValidate: false });

    if (hasDifferentRecipient) {
      if (baseInvoice.recipientName) {
        setValue('recipientName', baseInvoice.recipientName, { shouldValidate: false });
      }

      if (baseInvoice.recipientNip) {
        setValue('recipientNip', baseInvoice.recipientNip, { shouldValidate: false });
      }

      if (baseInvoice.recipientAddress) {
        const addressParts = baseInvoice.recipientAddress
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean);

        if (addressParts.length >= 2) {
          const [street, ...cityParts] = addressParts;
          setValue('recipientStreetNo', street, { shouldValidate: false });
          setValue('recipientAddress', cityParts.join(', '), { shouldValidate: false });
        } else {
          setValue('recipientStreetNo', '', { shouldValidate: false });
          setValue('recipientAddress', baseInvoice.recipientAddress, { shouldValidate: false });
        }
      }
    } else {
      // Ensure recipient fields stay clean if base invoice has no recipient snapshot.
      setValue('recipientName', '', { shouldValidate: false });
      setValue('recipientStreetNo', '', { shouldValidate: false });
      setValue('recipientAddress', '', { shouldValidate: false });
      setValue('recipientNip', '', { shouldValidate: false });
    }

    // ===================================================================
    // INVOICE ITEMS (deep clone from base invoice)
    // ===================================================================
    if (baseInvoice.items && baseInvoice.items.length > 0) {
      invoiceLogger.debug('[useCorrectionPrefill] 📦 Cloning items from baseInvoice:', {
        itemsCount: baseInvoice.items.length,
        firstItem: baseInvoice.items[0],
        firstItemVatType: baseInvoice.items[0]?.vatType,
      });

      const clonedItems = baseInvoice.items.map((item) => {
        const normalizedVatType = normalizeVatType(item.vatType);

        return {
          id: undefined, // Remove id for new correction items
          name: item.name || '',
          description: item.description || '',
          quantity: item.quantity || 1,
          unitNet: item.unitNet || 0,
          unitGross: item.unitGross || 0,
          vatType: normalizedVatType ?? VatRateType.Vat23, // ✅ Normalize with fallback to Vat23
          vatExemptNote: item.vatExemptNote || '',
        };
      });

      invoiceLogger.debug('[useCorrectionPrefill] ✅ Setting items:', clonedItems);
      invoiceLogger.debug('[useCorrectionPrefill] 🔄 VatType transformation:', {
        before: baseInvoice.items[0]?.vatType,
        after: clonedItems[0]?.vatType,
      });
      setValue('items', clonedItems, { shouldValidate: false });
    } else {
      invoiceLogger.debug('[useCorrectionPrefill] ⚠️ No items in baseInvoice to clone');
    }

    // ===================================================================
    // PAYMENT FIELDS (inherit from base if applicable)
    // ===================================================================
    if (baseInvoice.status) {
      setValue('status', baseInvoice.status, { shouldValidate: false });
    }

    if (baseInvoice.paidAmount !== undefined && baseInvoice.paidAmount !== null) {
      setValue('paidAmount', baseInvoice.paidAmount, { shouldValidate: false });
    }

    if (baseInvoice.paidAt) {
      setValue('paidAt', baseInvoice.paidAt.split('T')[0], { shouldValidate: false });
    }

    // ===================================================================
    // PFRON REFUND FIELDS (inherit from base if present)
    // ===================================================================
    if (baseInvoice.pfronRefundAmount !== undefined && baseInvoice.pfronRefundAmount !== null) {
      setValue('pfronRefundAmount', baseInvoice.pfronRefundAmount, { shouldValidate: false });
    }

    if (
      baseInvoice.pfronRefundPercentage !== undefined &&
      baseInvoice.pfronRefundPercentage !== null
    ) {
      setValue('pfronRefundPercentage', baseInvoice.pfronRefundPercentage, {
        shouldValidate: false,
      });
    }

    if (baseInvoice.pfronRefundNote) {
      setValue('pfronRefundNote', baseInvoice.pfronRefundNote, { shouldValidate: false });
    }

    // ===================================================================
    // NOTES (optional - inherit from base)
    // ===================================================================
    if (baseInvoice.notes) {
      setValue('notes', baseInvoice.notes, { shouldValidate: false });
    }

    // Clear selectedPrepaymentIds (not applicable for corrections)
    setValue('selectedPrepaymentIds', [], { shouldValidate: false });

    // Prefill complete
    setIsPrefilling(false);
  }, [baseInvoice, setValue]);
  // ✅ useRef prevents duplicate processing even though baseInvoice triggers effect on every render

  return {
    baseInvoice,
    isLoading: isPrefilling,
    error,
  };
};
